<template>
  <div class="resource-dashboard-container">
    <!-- 统一控制中心 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-left">
          <i class="el-icon-monitor dashboard-icon"></i>
          <span class="dashboard-title">{{ $t('resourceManagement.resourceDashboardTips') }}</span>
        </div>
        
        <div class="header-right">
          <div class="realtime-item">
            <span class="control-label">{{ $t('resourceManagement.realtimeOn') }}</span>
            <el-switch
              v-model="isRealtimeEnabled"
              active-color="#13ce66"
              size="small"
              @change="toggleRealtimeUpdates"
            ></el-switch>
          </div>
          
          <el-button 
            :loading="isUpdating" 
            type="primary" 
            size="small" 
            icon="el-icon-refresh"
            @click="handleManualRefresh"
            class="refresh-btn"
          >
            {{ $t('common.refresh') }}
          </el-button>
          
          <span v-if="lastUpdateTime" class="last-update-time">
            <i class="el-icon-time"></i>
            {{ formatDateTime(lastUpdateTime) }}
          </span>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="4" animated />
      <el-skeleton :rows="4" animated />
      <el-skeleton :rows="4" animated />
    </div>

    <!-- 主要内容 -->
    <div v-else class="dashboard-content">
      <!-- 资源概览卡片 -->
      <div class="overview-cards">
        <div class="overview-card model-total">
          <i class="bg-icon el-icon-cpu"></i>
          <div class="card-content">
            <div class="card-icon-wrapper">
              <i class="el-icon-cpu"></i>
            </div>
            <div class="card-info">
              <div class="card-title">{{ $t('resourceManagement.modelTotalResources') }}</div>
              <div class="card-value-wrapper">
                <span class="card-value">{{ formatNumber(overviewData.modelTotalResources) }}</span>
                <span class="card-unit">Tokens</span>
              </div>
            </div>
          </div>
        </div>

        <div class="overview-card user-total">
          <i class="bg-icon el-icon-user"></i>
          <div class="card-content">
            <div class="card-icon-wrapper">
              <i class="el-icon-user"></i>
            </div>
            <div class="card-info">
              <div class="card-title">{{ $t('resourceManagement.userTotalResources') }}</div>
              <div class="card-value-wrapper">
                <span class="card-value">{{ formatNumber(overviewData.userTotalResources) }}</span>
                <span class="card-unit">Tokens</span>
              </div>
            </div>
          </div>
        </div>

        <div class="overview-card used">
          <i class="bg-icon el-icon-pie-chart"></i>
          <div class="card-content">
            <div class="card-icon-wrapper">
              <i class="el-icon-pie-chart"></i>
            </div>
            <div class="card-info">
              <div class="card-title">{{ $t('resourceManagement.usedResources') }}</div>
              <div class="card-value-wrapper">
                <span class="card-value">{{ formatNumber(overviewData.usedResources) }}</span>
                <span class="card-unit">Tokens</span>
              </div>
              <div class="card-change" v-if="!isNaN(overviewData.usedResourcesChange) && overviewData.usedResourcesChange !== null">
                <i :class="overviewData.usedResourcesChange > 0 ? 'el-icon-caret-top icon-arrow' : 'el-icon-caret-bottom icon-arrow'"></i>
                <span class="change-text">{{ Math.abs(overviewData.usedResourcesChange) }}%</span>
              </div>
            </div>
          </div>
        </div>

        <div class="overview-card model-available">
          <i class="bg-icon el-icon-shopping-cart-full"></i>
          <div class="card-content">
            <div class="card-icon-wrapper">
              <i class="el-icon-shopping-cart-full"></i>
            </div>
            <div class="card-info">
              <div class="card-title">{{ $t('resourceManagement.modelAvailableResources') }}</div>
              <div class="card-value-wrapper">
                <span class="card-value">{{ formatNumber(overviewData.modelAvailableResources) }}</span>
                <span class="card-unit">Tokens</span>
              </div>
            </div>
          </div>
        </div>

        <div class="overview-card user-available">
          <i class="bg-icon el-icon-wallet"></i>
          <div class="card-content">
            <div class="card-icon-wrapper">
              <i class="el-icon-wallet"></i>
            </div>
            <div class="card-info">
              <div class="card-title">{{ $t('resourceManagement.userAvailableResources') }}</div>
              <div class="card-value-wrapper">
                <span class="card-value">{{ formatNumber(overviewData.availableResources) }}</span>
                <span class="card-unit">Tokens</span>
              </div>
            </div>
          </div>
        </div>

        <div class="overview-card rate">
          <i class="bg-icon el-icon-data-line"></i>
          <div class="card-content">
            <div class="card-icon-wrapper">
              <i class="el-icon-data-line"></i>
            </div>
            <div class="card-info">
              <div class="card-title">{{ $t('resourceManagement.utilizationRate') }}</div>
              <div class="card-value-wrapper">
                <span class="card-value">{{ overviewData.utilizationRate }}</span>
                <span class="card-unit">%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="main-content-grid">
        <!-- 第一行：资源趋势 (2/3) + 业务分布 (1/3) -->
        <div class="top-row">
          <el-card shadow="hover" class="trend-chart-card">
            <div slot="header" class="card-header">
              <div class="header-title-wrapper">
                <div class="title-icon-box">
                  <i class="el-icon-data-line"></i>
                </div>
                <div class="title-text-box">
                  <span class="header-main-title">{{ $t('resourceManagement.resourceUsageTrend') }}</span>
                  <el-tag v-if="chartForm.filterName" size="mini" type="primary" effect="plain" closable @close="handleResetFilter" class="filter-tag">
                    {{ chartForm.filterName }}
                  </el-tag>
                </div>
              </div>
              <div class="chart-controls">
                <div class="control-item">
                  <el-select 
                    v-model="chartForm.modelId" 
                    size="mini" 
                    filterable
                    class="custom-select model-select"
                    @change="handleModelChange"
                    :placeholder="$t('resourceManagement.modelPlaceholder')"
                  >
                    <el-option :label="$t('common.all')" value="all">
                      <i class="el-icon-menu" style="margin-right: 8px; color: #909399"></i>{{ $t('common.all') }}
                    </el-option>
                    <el-option
                      v-for="model in models"
                      :key="model.id"
                      :label="model.modelName"
                      :value="model.id"
                    >
                      <i class="el-icon-cpu" style="margin-right: 8px; color: #909399"></i>{{ model.modelName }}
                    </el-option>
                  </el-select>
                </div>
                
                <div class="control-item">
                  <el-radio-group v-model="chartForm.chartType" size="mini" @change="fetchResourceUsageData" class="custom-radio-group">
                    <el-tooltip :content="$t('resourceManagement.lineChart')" placement="top">
                      <el-radio-button label="line">
                        <i class="el-icon-data-line"></i>
                      </el-radio-button>
                    </el-tooltip>
                    <el-tooltip :content="$t('resourceManagement.barChart')" placement="top">
                      <el-radio-button label="bar">
                        <i class="el-icon-s-data"></i>
                      </el-radio-button>
                    </el-tooltip>
                  </el-radio-group>
                </div>

                <div class="control-item date-picker-wrapper" v-if="timeRange === 'custom'">
                  <el-date-picker
                    v-model="customDateRange"
                    type="daterange"
                    size="mini"
                    align="right"
                    unlink-panels
                    range-separator="-"
                    :start-placeholder="$t('common.startDate')"
                    :end-placeholder="$t('common.endDate')"
                    :picker-options="pickerOptions"
                    value-format="yyyy-MM-dd"
                    @change="handleCustomDateChange"
                    class="custom-date-picker"
                  >
                  </el-date-picker>
                </div>

                <div class="control-item">
                  <el-select v-model="timeRange" size="mini" class="custom-select time-select" @change="handleTimeRangeChange">
                    <el-option :label="$t('common.today')" value="today"></el-option>
                    <el-option :label="$t('common.week')" value="week"></el-option>
                    <el-option :label="$t('common.month')" value="month"></el-option>
                    <el-option :label="$t('common.quarter')" value="quarter"></el-option>
                    <el-option :label="$t('common.year')" value="year"></el-option>
                    <el-option :label="$t('common.custom')" value="custom"></el-option>
                  </el-select>
                </div>
              </div>
            </div>
            <div class="chart-container">
              <v-chart
                ref="trendChart"
                :option="chartOption"
                :autoresize="true"
                height="100%"
              ></v-chart>
            </div>
          </el-card>

          <el-card shadow="hover" class="analysis-card">
            <div slot="header" class="card-header">
              <div class="header-title-wrapper">
                <div class="title-icon-box" style="background: #f6ffed; color: #52c41a;">
                  <i class="el-icon-pie-chart"></i>
                </div>
                <div class="title-text-box">
                  <span class="header-main-title">{{ $t('resourceManagement.modelUsageRatio') }}</span>
                </div>
              </div>
              <div class="card-header-actions">
                <el-radio-group 
                  v-model="detailedStatsParams.metricType" 
                  size="mini" 
                  class="custom-radio-group" 
                  @change="fetchDetailedStats"
                >
                  <el-tooltip :content="$t('resourceManagement.callCount')" placement="top">
                    <el-radio-button label="callCount">
                      <i class="el-icon-phone-outline"></i>
                    </el-radio-button>
                  </el-tooltip>
                  <el-tooltip :content="$t('resourceManagement.tokenConsumption')" placement="top">
                    <el-radio-button label="tokenConsumption">
                      <i class="el-icon-coin"></i>
                    </el-radio-button>
                  </el-tooltip>
                </el-radio-group>
                
                <div class="control-item date-picker-wrapper" v-if="timeRange === 'custom'" style="margin-right: 10px;">
                  <el-date-picker
                    v-model="customDateRange"
                    type="daterange"
                    size="mini"
                    align="right"
                    unlink-panels
                    range-separator="-"
                    :start-placeholder="$t('common.startDate')"
                    :end-placeholder="$t('common.endDate')"
                    :picker-options="pickerOptions"
                    value-format="yyyy-MM-dd"
                    @change="handleCustomDateChange"
                    class="custom-date-picker"
                    style="width: 200px;"
                  >
                  </el-date-picker>
                </div>

                <el-select v-model="timeRange" size="mini" class="custom-select time-select" @change="handleTimeRangeChange">
                  <el-option :label="$t('common.today')" value="today"></el-option>
                  <el-option :label="$t('common.week')" value="week"></el-option>
                  <el-option :label="$t('common.month')" value="month"></el-option>
                  <el-option :label="$t('common.quarter')" value="quarter"></el-option>
                  <el-option :label="$t('common.year')" value="year"></el-option>
                  <el-option :label="$t('common.custom')" value="custom"></el-option>
                </el-select>

                <el-tooltip :content="$t('resourceManagement.dataLabel')" placement="top">
                  <el-button 
                    size="mini" 
                    :type="detailedStatsParams.showLabel ? 'primary' : 'default'"
                    class="action-icon-btn"
                    @click="detailedStatsParams.showLabel = !detailedStatsParams.showLabel; updateBizChartLabel()"
                  >
                    <i class="el-icon-price-tag"></i>
                  </el-button>
                </el-tooltip>
              </div>
            </div>
            <div class="chart-container-mini">
              <v-chart 
                ref="bizChart"
                :option="bizOption" 
                :autoresize="true" 
                height="100%"
                @click="handleBizChartClick"
              ></v-chart>
              <div v-if="isDetailedStatsLoading" class="mini-loading">
                <i class="el-icon-loading"></i>
              </div>
            </div>
          </el-card>
        </div>

        <!-- 第二行：模型排行 (1/2) + 用户排行 (1/2) -->
        <div class="middle-row">
          <el-card shadow="hover" class="usage-ranking-card">
            <div slot="header" class="card-header">
              <span class="header-main-title">{{ $t('resourceManagement.modelUsageRanking') }}</span>
            </div>
            <div class="ranking-container">
              <el-table
                :data="usageRanking"
                height="300"
                style="width: 100%"
                :show-header="true"
                :empty-text="$t('resourceManagement.noUsageData')"
                @row-click="handleModelClick"
                row-class-name="clickable-row"
              >
                <el-table-column type="index" :label="$t('common.rank')" width="60" align="center">
                  <template slot-scope="scope">
                    <span :class="['rank-badge', `rank-${scope.$index + 1}`]">{{ scope.$index + 1 }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="modelName" :label="$t('resourceManagement.modelName')" min-width="120" show-overflow-tooltip></el-table-column>
                <el-table-column prop="totalTokens" :label="$t('resourceManagement.tokensConsumed')" align="right" width="120">
                  <template slot-scope="scope">
                    <span class="tokens-value">{{ formatNumber(scope.row.totalTokens) }}</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-card>

          <el-card shadow="hover" class="usage-ranking-card">
            <div slot="header" class="card-header">
              <span class="header-main-title">{{ $t('resourceManagement.userUsageRanking') }}</span>
            </div>
            <div class="ranking-container">
              <el-table
                :data="userRanking"
                height="300"
                style="width: 100%"
                :show-header="true"
                @row-click="handleUserClick"
                row-class-name="clickable-row"
              >
                <el-table-column type="index" :label="$t('common.rank')" width="60" align="center">
                  <template slot-scope="scope">
                    <span :class="['rank-badge', `rank-${scope.$index + 1}`]">{{ scope.$index + 1 }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="username" :label="$t('resourceManagement.userName')" min-width="120"></el-table-column>
                <el-table-column prop="totalTokens" :label="$t('resourceManagement.tokensConsumed')" align="right" width="120">
                  <template slot-scope="scope">
                    <span class="tokens-value">{{ formatNumber(scope.row.totalTokens) }}</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-card>
        </div>

        <!-- 资源告警管理 -->
        <el-card shadow="hover" class="alerts-card-full">
          <div slot="header" class="card-header">
            <span>{{ $t('resourceManagement.resourceAlerts') }}</span>
            <div class="header-actions">
              <el-button type="primary" size="mini" @click="handleCheckAlerts">
                <i class="el-icon-refresh"></i>
                {{ $t('resourceManagement.checkNow') }}
              </el-button>
              <el-button type="danger" size="mini" @click="handleClearAlerts" :disabled="alerts.length === 0">
                <i class="el-icon-delete"></i>
                {{ $t('common.clear') }}
              </el-button>
            </div>
          </div>
          <div class="alert-table-container">
            <el-table
              v-loading="alertsLoading"
              :data="alerts"
              border
              style="width: 100%"
              height="400"
              size="small"
            >
              <el-table-column prop="alertType" :label="$t('resourceManagement.alertType')" width="120">
                <template slot-scope="scope">
                  <el-tag :type="getAlertTypeTagColor(scope.row.alertType)" size="mini">
                    {{ getAlertTypeName(scope.row.alertType) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="title" :label="$t('resourceManagement.alertTitle')" min-width="150" show-overflow-tooltip></el-table-column>
              <el-table-column prop="content" :label="$t('resourceManagement.alertDescription')" min-width="200" show-overflow-tooltip></el-table-column>
              <el-table-column prop="alertLevel" :label="$t('resourceManagement.alertLevel')" width="100">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.alertLevel === 'CRITICAL' ? 'danger' : 'warning'" size="mini">
                    {{ scope.row.alertLevel === 'CRITICAL' ? $t('resourceManagement.critical') : $t('resourceManagement.warning') }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="createDate" :label="$t('resourceManagement.occurTime')" width="160">
                <template slot-scope="scope">
                  {{ formatDateTime(scope.row.createDate) }}
                </template>
              </el-table-column>
              <el-table-column prop="status" :label="$t('common.status')" width="100">
                <template slot-scope="scope">
                  <el-tag :type="getAlertStatusTagColor(scope.row.status)" size="mini">
                    {{ getAlertStatusName(scope.row.status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column :label="$t('common.operation')" width="180" fixed="right">
                <template slot-scope="scope">
                  <el-button size="mini" type="primary" plain @click="handleViewAlert(scope.row)">{{ $t('common.detail') }}</el-button>
                  <template v-if="scope.row.status === 0">
                    <el-button
                      size="mini"
                      class="btn-action-audit"
                      plain
                      @click="handleAlert(scope.row)"
                    >{{ $t('common.process') }}</el-button>
                    <el-button
                      size="mini"
                      class="btn-action-status"
                      plain
                      @click="handleIgnoreAlert(scope.row)"
                    >{{ $t('common.ignore') }}</el-button>
                  </template>
                </template>
              </el-table-column>
            </el-table>
            <div class="pagination-container">
              <el-pagination
                @size-change="handleAlertSizeChange"
                @current-change="handleAlertCurrentChange"
                :current-page="alertPagination.page"
                :page-sizes="[5, 10, 20, 50]"
                :page-size="alertPagination.pageSize"
                layout="total, sizes, prev, pager, next"
                :total="alertPagination.total"
                small
              ></el-pagination>
            </div>
          </div>
        </el-card>
      </div>
    </div>
      <!-- 告警详情对话框 -->
    <el-dialog
      :title="$t('resourceManagement.alertDetail')"
      :visible.sync="alertDialogVisible"
      width="550px"
      append-to-body
      custom-class="alert-detail-dialog"
    >
      <div v-if="currentAlert" class="alert-detail-content">
        <div class="alert-header-info">
          <div class="alert-title-row">
            <span class="detail-label">{{ $t('resourceManagement.alertTitle') }}:</span>
            <span class="detail-value title-text">{{ currentAlert.title }}</span>
          </div>
          <div class="alert-tags-row">
                <el-tag :type="getAlertTypeTagColor(currentAlert.alertType)" size="small" effect="dark">
                  {{ getAlertTypeName(currentAlert.alertType) }}
                </el-tag>
                <el-tag :type="currentAlert.alertLevel === 'CRITICAL' ? 'danger' : 'warning'" size="small" effect="plain" style="margin-left: 10px;">
                  {{ currentAlert.alertLevel === 'CRITICAL' ? $t('resourceManagement.critical') : $t('resourceManagement.warning') }}
                </el-tag>
              </div>
        </div>

        <el-divider></el-divider>

        <div class="alert-body-info">
          <div class="info-item">
            <span class="item-label"><i class="el-icon-time"></i> {{ $t('resourceManagement.occurTime') }}:</span>
            <span class="item-value">{{ formatDateTime(currentAlert.createDate) }}</span>
          </div>
          
          <div class="info-item">
            <span class="item-label"><i class="el-icon-info"></i> {{ $t('resourceManagement.alertDescription') }}:</span>
            <div class="item-value content-box">{{ currentAlert.content }}</div>
          </div>

          <!-- 模型信息 -->
          <div v-if="currentAlert.modelId" class="info-item highlight-item">
            <span class="item-label"><i class="el-icon-cpu"></i> {{ $t('resourceManagement.modelName') }}:</span>
            <div class="item-value link-value">
              <span class="name-text">{{ currentAlert.modelName || currentAlert.modelId }}</span>
              <el-button 
                v-if="currentAlert.targetUrl" 
                type="primary" 
                size="mini" 
                plain
                icon="el-icon-right" 
                @click="handleAlertJump(currentAlert.targetUrl)"
                class="jump-btn"
              >
                {{ $t('resourceManagement.viewModelDetail') }}
              </el-button>
            </div>
          </div>
          
          <!-- 用户信息 -->
          <div v-if="currentAlert.userId" class="info-item highlight-item">
            <span class="item-label"><i class="el-icon-user"></i> {{ $t('resourceManagement.userName') }}:</span>
            <div class="item-value link-value">
              <span class="name-text">{{ currentAlert.username || currentAlert.userId }}</span>
              <el-button 
                v-if="currentAlert.targetUrl" 
                type="primary" 
                size="mini" 
                plain
                icon="el-icon-right" 
                @click="handleAlertJump(currentAlert.targetUrl)"
                class="jump-btn"
              >
                {{ $t('resourceManagement.viewUserInfo') }}
              </el-button>
            </div>
          </div>

          <div class="info-item" v-if="currentAlert.remark">
            <span class="item-label"><i class="el-icon-edit-outline"></i> {{ $t('common.remark') }}:</span>
            <span class="item-value">{{ currentAlert.remark }}</span>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="alertDialogVisible = false" size="small">{{ $t('common.close') }}</el-button>
        <el-button 
          v-if="currentAlert && currentAlert.status === 0" 
          type="success" 
          size="small"
          icon="el-icon-check"
          @click="handleResolveAlert(currentAlert)"
        >
          {{ $t('resourceManagement.markAsResolved') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import ECharts from '@/components/charts/ECharts.vue'
import { getResourceAlerts, resolveResourceAlert, ignoreResourceAlert, checkResourceAlerts } from '@/apis/resource/resource'

export default {
  name: 'ResourceDashboard',
  components: {
    'v-chart': ECharts
  },
  data() {
    return {
      // 加载状态
      loading: false,
      
      // 模型列表
      models: [],
      
      // 概览数据
      overviewData: {
        modelTotalResources: 0,
        userTotalResources: 0,
        totalResources: 0,
        usedResources: 0,
        availableResources: 0,
        modelAvailableResources: 0,
        utilizationRate: 0,
        totalResourcesChange: 0,
        usedResourcesChange: 0,
        availableResourcesChange: 0,
        utilizationRateChange: 0
      },
      
      // 图表配置
      chartForm: {
        modelId: 'all',
        userId: null,
        filterName: '',
        chartType: 'line',
        showLabel: false
      },
      chartOption: {
        grid: { left: '30', right: '30', bottom: '60', top: '40', containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: [] },
        yAxis: { type: 'value' },
        series: []
      },
      
      // 时间范围
      timeRange: 'month',
      customDateRange: [],
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now();
        },
        shortcuts: [{
          text: '最近一周',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: '最近一个月',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: '最近三个月',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
            picker.$emit('pick', [start, end]);
          }
        }]
      },
      
      // 配额列表
      quotaList: [],
      
      // 告警列表
      alerts: [],
      alertPagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      alertsLoading: false,
      
      // 资源使用排行榜
      usageRanking: [],
      
      // 用户使用排行榜
      userRanking: [],
      
      // 详细统计参数
      isDetailedStatsLoading: false,
      detailedStatsParams: {
        metricType: 'callCount',
        granularity: 'week',
        showLabel: false,
        modelUsageStats: [] // 存储原始统计数据
      },
      
      // 业务分布图表
      bizOption: {
        tooltip: { trigger: 'item' },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          data: []
        }]
      },
      
      // 实时更新相关
      isRealtimeEnabled: false,
      isUpdating: false,
      realtimeInterval: null,
      realtimeIntervalTime: 30000, // 30秒
      lastUpdateTime: null,
      updateInterval: null,

      // 告警详情对话框
      alertDialogVisible: false,
      currentAlert: null
    }
  },
  computed: {
    // 计算属性，可以在这里进行数据计算
  },
  watch: {
    // 监听数据变化
    timeRange(newVal) {
      this.handleTimeRangeChange(newVal)
    }
  },
  created() {
    // 组件创建时的初始化逻辑
    this.fetchDashboardData()
  },
  mounted() {
    // 组件挂载完成后的逻辑
    this.initRealtimeUpdates()
  },
  destroyed() {
    // 组件销毁时清理定时器
    this.stopRealtimeUpdates()
  },
  methods: {
    // 获取仪表板数据
    async fetchDashboardData() {
      this.loading = true
      try {
        // 先获取模型列表，以便其他请求使用
        await this.fetchModels()
        
        // 并行获取其他数据
        await Promise.all([
          this.fetchResourceOverview(),
          this.fetchResourceUsageData(),
          this.fetchAlertList(),
          this.fetchDetailedStats()
        ])
      } catch (error) {
        console.error('获取仪表板数据失败:', error)
        this.$message.error(this.$t('common.loadFailed') + '：' + error.message)
      } finally {
        this.loading = false
      }
    },

    // 获取模型列表
    async fetchModels() {
      try {
        const results = await Promise.allSettled([
          this.$store.dispatch('model/fetchModelList', {
            pageNum: 1,
            pageSize: 100,
            modelType: 'LLM'
          }),
          this.$store.dispatch('model/fetchModelList', {
            pageNum: 1,
            pageSize: 100,
            modelType: 'TTS'
          })
        ])
        const llmModels = (results[0].status === 'fulfilled' && results[0].value && results[0].value.list) ? results[0].value.list : []
        const ttsModels = (results[1].status === 'fulfilled' && results[1].value && results[1].value.list) ? results[1].value.list : []
        this.models = [...llmModels, ...ttsModels]
      } catch (error) {
        this.models = []
      }
    },
    
    async fetchDetailedStats() {
      this.isDetailedStatsLoading = true
      try {
        const params = {
          timeRange: this.timeRange,
          granularity: this.detailedStatsParams.granularity
        }
        if (this.timeRange === 'custom' && this.customDateRange && this.customDateRange.length === 2) {
          params.startDate = this.customDateRange[0]
          params.endDate = this.customDateRange[1]
        }
        
        const data = await this.$store.dispatch('resource/fetchDetailedStats', params)
        this.userRanking = (data && data.userRanking) || []
        this.usageRanking = (data && data.modelRanking) || []
        this.detailedStatsParams.modelUsageStats = (data && data.modelUsageStats) || []
        this.initBizChart(this.detailedStatsParams.modelUsageStats)
        
        // 确保图表在数据加载后重新调整大小
        this.$nextTick(() => {
          if (this.$refs.bizChart) this.$refs.bizChart.resize()
        })
        
        // 同时也更新趋势图，因为粒度可能改变了
        this.fetchResourceUsageData()
      } catch (error) {
        console.error('获取详细统计数据失败:', error)
        this.initBizChart([])
      } finally {
        this.isDetailedStatsLoading = false
      }
    },

    initBizChart(data) {
      if (!data || data.length === 0) {
        this.bizOption = {
          title: {
            text: this.$t('resourceManagement.noUsageData'),
            left: 'center',
            top: 'center',
            textStyle: { color: '#909399', fontSize: 14, fontWeight: 'normal' }
          },
          series: []
        }
        return
      }

      const metricKey = this.detailedStatsParams.metricType === 'callCount' ? 'callCount' : 'totalTokens'
      
      // 更加现代化的配色方案 - 采用 Ant Design 风格的柔和色彩
      const colors = [
        '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
        '#722ed1', '#13c2c2', '#eb2f96', '#2f54eb', '#fa8c16'
      ]

      this.bizOption = {
        color: colors,
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderColor: '#f0f2f5',
          borderWidth: 1,
          padding: [12, 16],
          extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-radius: 8px;',
          textStyle: { color: '#303133', fontSize: 13 },
          formatter: (params) => {
            const item = data.find(d => d.modelName === params.name)
            const callCount = item ? this.formatNumber(item.callCount) : 0
            const totalTokens = item ? this.formatNumber(item.totalTokens) : 0
            return `<div style="margin-bottom: 8px; font-weight: 600; color: #303133; font-size: 14px; border-bottom: 1px solid #f0f2f5; padding-bottom: 6px;">${params.name}</div>
                    <div style="display: flex; justify-content: space-between; gap: 24px; margin-bottom: 4px;">
                      <span style="color: #606266;">${this.$t('resourceManagement.callCount')}:</span>
                      <span style="font-weight: 600; color: #303133;">${callCount}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; gap: 24px; margin-bottom: 4px;">
                      <span style="color: #606266;">${this.$t('resourceManagement.tokenConsumption')}:</span>
                      <span style="font-weight: 600; color: #303133;">${totalTokens}</span>
                    </div>
                    <div style="margin-top: 8px; padding-top: 6px; border-top: 1px dashed #f0f2f5; display: flex; justify-content: space-between;">
                      <span style="color: #606266;">占比:</span>
                      <span style="font-weight: 600; color: #409EFF;">${params.percent}%</span>
                    </div>`
          }
        },
        legend: { 
          bottom: '0', 
          left: 'center', 
          icon: 'circle', 
          itemWidth: 10,
          itemGap: 16,
          textStyle: { color: '#909399', fontSize: 12 },
          type: 'scroll'
        },
        series: [
          {
            name: this.$t('resourceManagement.modelUsageRatio'),
            type: 'pie',
            radius: ['48%', '72%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: true,
            itemStyle: { 
              borderRadius: 6, 
              borderColor: '#fff', 
              borderWidth: 2
            },
            label: { 
              show: this.detailedStatsParams.showLabel,
              position: this.detailedStatsParams.showLabel ? 'outside' : 'center',
              formatter: '{b}: {d}%',
              color: '#606266',
              fontSize: 11
            },
            emphasis: { 
              scale: true,
              scaleSize: 6,
              itemStyle: {
                shadowBlur: 15,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.1)'
              },
              label: { 
                show: true, 
                fontSize: 16, 
                fontWeight: 'bold',
                color: '#303133',
                formatter: '{b}\n{d}%'
              } 
            },
            data: data.map(item => ({
              name: item.modelName,
              value: item[metricKey],
              modelId: item.modelId
            }))
          }
        ]
      }
    },

    // 更新环形图标签显示
    updateBizChartLabel() {
      this.initBizChart(this.detailedStatsParams.modelUsageStats)
    },

    // 处理环形图点击事件（下钻）
    handleBizChartClick(params) {
      if (params.data && params.data.modelId) {
        // 同步详细统计中的聚合粒度到趋势图（如果适用）
        // 注意：此处我们希望趋势图的时间范围和粒度与当前饼图统计时一致
        this.handleModelClick({
          modelId: params.data.modelId,
          modelName: params.data.name
        })
      }
    },

    getBusinessTypeName(type) {
      const map = {
        'CHAT': this.$t('resourceManagement.bizChat'),
        'STT': this.$t('resourceManagement.bizSTT'),
        'TTS': this.$t('resourceManagement.bizTTS'),
        'VAD': this.$t('resourceManagement.bizVAD'),
        'OCR': this.$t('resourceManagement.bizOCR')
      }
      return map[type] || type
    },

    formatNumber(num) {
      if (!num) return '0'
      if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toString()
    },

    // 初始化图表
    initChart(trendData) {
      const isLine = this.chartForm.chartType === 'line'
      
      // 如果没有数据，显示空坐标轴
      if (!trendData || !trendData.labels || trendData.labels.length === 0) {
        this.chartOption = {
          title: {
            text: this.$t('resourceManagement.noUsageData'),
            left: 'center',
            top: 'center',
            textStyle: {
              color: '#909399',
              fontSize: 14,
              fontWeight: 'normal'
            }
          },
          grid: { left: '30', right: '30', bottom: '60', top: '40', containLabel: true },
          xAxis: { 
            type: 'category', 
            data: [],
            axisLabel: {
              interval: 'auto',
              rotate: 30
            },
            axisLine: { lineStyle: { color: '#909399' } }
          },
          yAxis: { 
            type: 'value',
            axisLine: { lineStyle: { color: '#909399' } },
            splitLine: { lineStyle: { type: 'dashed' } }
          },
          series: []
        }
        return
      }
      
      const series = [{
        name: this.$t('resourceManagement.tokensConsumed'),
        type: this.chartForm.chartType,
        symbol: isLine ? 'circle' : 'none',
        symbolSize: 8,
        showSymbol: false,
        areaStyle: isLine ? {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
            { offset: 1, color: 'rgba(64, 158, 255, 0)' }
          ])
        } : null,
        data: trendData.tokens || [],
        smooth: 0.45,
        label: {
          show: this.chartForm.showLabel,
          position: 'top',
          fontSize: 10,
          color: '#606266',
          formatter: (params) => this.formatNumber(params.value)
        },
        itemStyle: { 
          color: isLine ? '#409EFF' : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#66b1ff' },
            { offset: 1, color: '#409eff' }
          ]),
          borderRadius: [4, 4, 0, 0]
        },
        lineStyle: {
          width: 3,
          color: '#409EFF',
          shadowColor: 'rgba(64, 158, 255, 0.2)',
          shadowBlur: 10,
          shadowOffsetY: 4
        },
        emphasis: {
          focus: 'series',
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.2)'
          }
        },
        markLine: {
          silent: true,
          symbol: 'none',
          label: {
            position: 'insideEndTop',
            formatter: (params) => `${params.name}: ${this.formatNumber(params.value)}`,
            fontSize: 10,
            fontWeight: 500,
            color: '#909399',
            padding: [4, 8],
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 4,
            distance: [0, 10],
            shadowBlur: 4,
            shadowColor: 'rgba(0,0,0,0.1)'
          },
          lineStyle: {
            type: 'dashed',
            color: 'rgba(144, 147, 153, 0.3)',
            width: 1
          },
          data: [
            { type: 'average', name: this.$t('resourceManagement.average') }
          ]
        }
      }]

      this.chartOption = {
        title: { show: false },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          borderColor: '#f0f2f5',
          borderWidth: 1,
          padding: [12, 16],
          textStyle: { color: '#303133', fontSize: 13 },
          extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-radius: 8px;',
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(64, 158, 255, 0.2)',
              width: 1
            }
          },
          formatter: (params) => {
            let res = `<div style="font-weight: 600; margin-bottom: 8px; color: #303133; font-size: 14px;">${params[0].name}</div>`;
            params.forEach(item => {
              res += `<div style="display: flex; align-items: center; justify-content: space-between; gap: 24px;">
                <span style="display: flex; align-items: center; color: #606266;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${item.color}; margin-right: 8px;"></span>
                  ${item.seriesName}
                </span>
                <span style="font-weight: 600; color: #303133">${this.formatNumber(item.value)}</span>
              </div>`;
            });
            return res;
          }
        },
        legend: {
          show: true,
          top: '0',
          right: '25',
          icon: 'circle',
          itemWidth: 8,
          textStyle: { fontSize: 12, color: '#909399' }
        },
        grid: {
          left: '10',
          right: '25',
          bottom: '10',
          top: '50',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            boundaryGap: !isLine,
            data: trendData.labels || [],
            axisLabel: {
              color: '#909399',
              fontSize: 11,
              interval: 'auto',
              margin: 12,
              rotate: trendData.labels.length > 10 ? 30 : 0
            },
            axisLine: { lineStyle: { color: '#f0f2f5' } },
            axisTick: { show: false }
          }
        ],
        yAxis: [
          {
            type: 'value',
            axisLabel: {
              color: '#909399',
              fontSize: 11,
              margin: 12,
              formatter: (value) => this.formatNumber(value)
            },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { 
              lineStyle: { 
                type: 'solid',
                color: '#f5f7fa'
              } 
            }
          }
        ],
        series: series
      }
    },
    
    // 处理时间范围变化
    async handleTimeRangeChange(val) {
      if (val !== 'custom') {
        this.customDateRange = []
      }
      // 当全局时间范围改变时，同步更新详细统计的粒度建议（可选，但通常用户希望看到对应范围的统计）
      // 例如：今天 -> 小时，周 -> 天，月 -> 周
      if (val === 'today') this.detailedStatsParams.granularity = 'hour'
      else if (val === 'week') this.detailedStatsParams.granularity = 'day'
      else if (val === 'month') this.detailedStatsParams.granularity = 'week'
      
      await Promise.all([
        this.fetchResourceUsageData(),
        this.fetchDetailedStats()
      ])
    },

    // 处理自定义日期变化
    async handleCustomDateChange(val) {
      if (val && val.length === 2) {
        await Promise.all([
          this.fetchResourceUsageData(),
          this.fetchDetailedStats()
        ])
      }
    },

    // 告警管理相关方法
    async handleCheckAlerts() {
      try {
        await checkResourceAlerts()
        this.$message.success(this.$t('resourceManagement.alertCheckSuccess') || '资源预警检查完成')
        this.fetchAlertList()
      } catch (error) {
        console.error('手动触发预警检查失败:', error)
        this.$message.error(this.$t('resourceManagement.alertCheckFailed') || '手动触发预警检查失败')
      }
    },

    async fetchAlertList() {
      this.alertsLoading = true
      try {
        const params = {
          page: this.alertPagination.page,
          pageSize: this.alertPagination.pageSize
        }
        const response = await getResourceAlerts(params)
        if (response && response.list) {
          this.alerts = response.list
          this.alertPagination.total = response.total
        }
      } catch (error) {
        console.error('获取告警列表失败:', error)
        this.$message.error(this.$t('resourceManagement.fetchAlertListFailed') || '获取告警列表失败')
      } finally {
        this.alertsLoading = false
      }
    },

    handleAlertSizeChange(val) {
      this.alertPagination.pageSize = val
      this.fetchAlertList()
    },

    handleAlertCurrentChange(val) {
      this.alertPagination.page = val
      this.fetchAlertList()
    },

    async handleClearAlerts() {
      try {
        await this.$confirm(this.$t('resourceManagement.confirmClearAlerts'), this.$t('common.tips'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        
        // 调用处理所有告警的 API，这里假设后端支持批量处理或清空
        // 如果后端没有专门的清空接口，可以循环处理或提示暂不支持
        this.$message.info(this.$t('common.comingSoon'))
        
        // this.fetchAlertList()
      } catch (error) {
        // 取消
      }
    },

    handleViewAlert(row) {
      this.currentAlert = { ...row }
      this.alertDialogVisible = true
    },

    handleAlertJump(url) {
      this.alertDialogVisible = false
      if (url) {
        this.$router.push(url)
      }
    },

    async handleResolveAlert(alert) {
      try {
        await this.$confirm(this.$t('resourceManagement.confirmResolveAlert'), this.$t('common.tips'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'info'
        })
        
        await this.$store.dispatch('resource/resolveAlert', {
          alertId: alert.id,
          remark: '用户手动处理'
        })
        
        this.$message.success(this.$t('common.success'))
        this.alertDialogVisible = false
        this.fetchAlertList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('处理告警失败:', error)
          this.$message.error(this.$t('common.operationFailed'))
        }
      }
    },

    getResourceTypeTagColor(type) {
      const colorMap = {
        compute: 'primary',
        storage: 'success',
        network: 'warning',
        memory: 'danger'
      }
      return colorMap[type] || 'info'
    },

    getAlertTypeTagColor(type) {
      if (!type) return 'info'
      const lowerType = type.toLowerCase()
      const colorMap = {
        'concurrency': 'warning',
        'balance': 'danger',
        'anomaly': 'danger',
        'usage': 'warning',
        'model_quota': 'danger',
        'user_quota': 'danger',
        'error_rate': 'danger'
      }
      return colorMap[lowerType] || 'info'
    },

    getAlertTypeName(type) {
      if (!type) return type
      const lowerType = type.toLowerCase()
      const types = {
        'concurrency': this.$t('resourceManagement.concurrencyAlert'),
        'balance': this.$t('resourceManagement.balanceAlert'),
        'anomaly': this.$t('resourceManagement.anomalyAlert'),
        'usage': this.$t('resourceManagement.usageAlert'),
        'model_quota': this.$t('resourceManagement.modelQuotaAlert'),
        'user_quota': this.$t('resourceManagement.userQuotaAlert'),
        'error_rate': this.$t('resourceManagement.errorRateAlert')
      }
      return types[lowerType] || type
    },

    getAlertStatusTagColor(status) {
      const colorMap = {
        0: 'danger',   // 待处理
        1: 'info',     // 已忽略
        2: 'success'   // 已解决
      }
      return colorMap[status] || 'info'
    },

    getAlertStatusName(status) {
      const statuses = {
        0: this.$t('resourceManagement.statusUnprocessed'),
        1: this.$t('resourceManagement.statusIgnored'),
        2: this.$t('resourceManagement.statusResolved')
      }
      return statuses[status] || status
    },

    getAlertLevelTagColor(level) {
      const colorMap = {
        'info': 'info',
        'warning': 'warning',
        'error': 'danger',
        'critical': 'danger'
      }
      return colorMap[level] || 'info'
    },

    getAlertLevelName(level) {
      const levels = {
        'info': this.$t('resourceManagement.info'),
        'warning': this.$t('resourceManagement.warning'),
        'error': this.$t('resourceManagement.error'),
        'critical': this.$t('resourceManagement.critical')
      }
      return levels[level.toLowerCase()] || level
    },
    
    // 查看配额
    handleViewQuota() {
      // 导航到配额管理页面
      this.$router.push('/resource/quota')
    },
    
    // 处理告警
    async handleAlert(alert) {
      try {
        const { value: remark } = await this.$prompt(this.$t('resourceManagement.enterProcessRemark'), this.$t('common.tips'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          inputPlaceholder: this.$t('resourceManagement.remarkPlaceholder')
        })
        
        await resolveResourceAlert(alert.id, remark)
        
        this.$message.success(this.$t('resourceManagement.alertProcessed'))
        // 更新告警列表
        this.fetchAlertList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('处理告警失败:', error)
          this.$message.error(this.$t('common.operationFailed'))
        }
      }
    },

    // 忽略告警
    async handleIgnoreAlert(alert) {
      try {
        await this.$confirm(this.$t('resourceManagement.confirmIgnoreAlert'), this.$t('common.tips'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        
        await ignoreResourceAlert(alert.id)
        
        this.$message.success(this.$t('resourceManagement.alertIgnored'))
        // 更新告警列表
        this.fetchAlertList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('忽略告警失败:', error)
          this.$message.error(this.$t('common.operationFailed'))
        }
      }
    },
    
    // 导航到告警页面
    goToAlerts() {
      // 如果需要专门的告警历史页面，可以在此跳转
      // this.$router.push('/resource/alerts')
    },
    
    // 获取资源类型名称
    getResourceTypeName(type) {
      const typeMap = {
        compute: this.$t('resourceManagement.computeResources'),
        storage: this.$t('resourceManagement.storageResources'),
        network: this.$t('resourceManagement.networkResources'),
        memory: this.$t('resourceManagement.memoryResources')
      }
      return typeMap[type] || type
    },
    
    // 获取使用率颜色
    getUtilizationColor(utilization) {
      if (utilization < 50) return '#67C23A'
      if (utilization < 80) return '#E6A23C'
      return '#F56C6C'
    },
    
    // 实时更新相关方法
    initRealtimeUpdates() {
      // 初始化时启动实时更新
      this.startRealtimeUpdates()
    },
    
    startRealtimeUpdates() {
      if (this.realtimeInterval) {
        clearInterval(this.realtimeInterval)
      }
      
      // 设置实时更新定时器
      this.realtimeInterval = setInterval(() => {
        if (!this.isUpdating && document.visibilityState === 'visible') {
          this.updateAllData()
        }
      }, this.realtimeIntervalTime)
      
      this.isRealtimeEnabled = true
      this.lastUpdateTime = new Date()
    },
    
    stopRealtimeUpdates() {
      if (this.realtimeInterval) {
        clearInterval(this.realtimeInterval)
        this.realtimeInterval = null
      }
      this.isRealtimeEnabled = false
    },
    
    toggleRealtimeUpdates() {
      if (this.isRealtimeEnabled) {
        this.startRealtimeUpdates()
        this.$message.success(this.$t('resourceManagement.realtimeUpdatesEnabled'))
      } else {
        this.stopRealtimeUpdates()
        this.$message.info(this.$t('resourceManagement.realtimeUpdatesDisabled'))
      }
    },
    
    async handleManualRefresh() {
      await this.updateAllData()
    },
    
    async updateAllData() {
      if (this.isUpdating) return
      
      this.isUpdating = true
      const updateTime = new Date()
      
      try {
        // 并行更新数据
        const promises = [
          this.fetchResourceOverview(),
          this.fetchResourceUsageData(),
          this.fetchDetailedStats(),
          this.fetchAlertList(),
          this.fetchRealtimeLogs()
        ]
        
        await Promise.allSettled(promises)
        this.lastUpdateTime = updateTime
        
        // 显示更新时间提示
        this.$nextTick(() => {
          const timeStr = updateTime.toLocaleTimeString('zh-CN', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })
          console.log(`数据已更新: ${timeStr}`)
        })
        
      } catch (error) {
        console.error('实时数据更新失败:', error)
      } finally {
        this.isUpdating = false
      }
    },
    
    async fetchResourceOverview() {
      try {
        const data = await this.$store.dispatch('resource/fetchResourceOverview')
        if (data) {
          this.overviewData = data
        }
      } catch (error) {
        console.error('获取概览数据失败:', error)
      }
    },
    
    async fetchResourceUsageData() {
      try {
        // 获取趋势数据
        const trendParams = {
          timeRange: this.timeRange,
          modelId: this.chartForm.modelId,
          userId: this.chartForm.userId,
          granularity: this.detailedStatsParams.granularity
        }
        
        // 如果是自定义时间范围，添加开始和结束日期
        if (this.timeRange === 'custom' && this.customDateRange && this.customDateRange.length === 2) {
          trendParams.startDate = this.customDateRange[0]
          trendParams.endDate = this.customDateRange[1]
        }
        
        const trendData = await this.$store.dispatch('resource/fetchResourceUsageTrend', trendParams)
        this.initChart(trendData)
        
        // 确保图表在数据加载后重新调整大小
        this.$nextTick(() => {
          if (this.$refs.trendChart) this.$refs.trendChart.resize()
        })
      } catch (error) {
        console.error('获取资源使用数据失败:', error)
        this.initChart(null)
      }
    },

    // 处理模型下拉切换
    handleModelChange(val) {
      if (val === 'all') {
        this.chartForm.filterName = ''
      } else {
        const model = this.models.find(m => m.id === val)
        this.chartForm.filterName = model ? model.modelName : ''
      }
      this.chartForm.userId = null // 切换模型时清除用户过滤
      this.fetchResourceUsageData()
    },

    // 重置过滤
    handleResetFilter() {
      this.chartForm.modelId = 'all'
      this.chartForm.userId = null
      this.chartForm.filterName = ''
      this.fetchResourceUsageData()
    },

    // 点击模型排行项钻取
    handleModelClick(row) {
      this.chartForm.modelId = row.id || row.modelId
      this.chartForm.userId = null
      this.chartForm.filterName = row.modelName
      
      // 确保时间范围和聚合粒度一致
      // 当从饼图点击钻取时，将趋势图的粒度同步为当前统计饼图时使用的粒度
      // 这样趋势图展现的数据点密度将与饼图统计的维度保持逻辑一致
      this.fetchResourceUsageData()
      
      // 滚动到趋势图
      this.$nextTick(() => {
        const chartEl = document.querySelector('.trend-chart-card')
        if (chartEl) {
          chartEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },

    // 点击用户排行项钻取
    handleUserClick(row) {
      this.chartForm.userId = row.userId || row.id
      this.chartForm.modelId = 'all'
      this.chartForm.filterName = row.username || row.userName
      this.fetchResourceUsageData()
      // 滚动到趋势图
      this.$nextTick(() => {
        const chartEl = document.querySelector('.trend-chart-card')
        if (chartEl) {
          chartEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    
    // 获取排行榜进度条颜色
    getRankingColor(usage) {
      if (usage > 90) return '#F56C6C'
      if (usage > 70) return '#E6A23C'
      return '#409EFF'
    },
    
    // 格式化日期时间
    formatDateTime(date) {
      if (!date) return ''
      
      const d = new Date(date)
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped lang="scss">
.resource-dashboard-container {
  padding: 10px;
  background-color: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* 隐藏滚动条 */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  .control-panel {
    flex-shrink: 0;
    padding: 0 0 10px 0;
    border-bottom: none;

    .control-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;

      .header-left {
        display: flex;
        align-items: center;
        gap: 10px;
        color: #64748b;
        font-size: 14px;

        .dashboard-icon {
          font-size: 18px;
          color: #409eff;
        }

        .dashboard-title {
          font-size: 14px;
          font-weight: normal;
          color: #64748b;
          margin: 0;
        }
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 12px;

        .realtime-item {
          display: flex;
          align-items: center;
          gap: 8px;

          .control-label {
            font-size: 13px;
            color: #64748b;
          }
        }

        .refresh-btn {
          border-radius: 8px;
          font-weight: 500;
        }

        .last-update-time {
          font-size: 12px;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 4px;
          padding-left: 12px;
          border-left: 1px solid #f1f5f9;
        }
      }
    }
  }

  .loading-container {
    padding: 20px;
    flex: 1;
    overflow-y: auto;
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari */
    }
  }

  .dashboard-content {
    flex: 1;
    padding: 12px 10px 10px 10px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;

    /* 隐藏滚动条 */
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari */
    }

    .overview-cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      flex-shrink: 0;
      margin-bottom: 4px;
      padding: 0;

      .overview-card {
        border-radius: 16px;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        padding: 24px;
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-height: 140px;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
        will-change: transform, box-shadow;

        &:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
          
          .bg-icon {
            transform: scale(1.1) rotate(-5deg);
            opacity: 0.15;
          }
        }

        .bg-icon {
          position: absolute;
          right: -10px;
          bottom: -10px;
          font-size: 100px;
          color: #fff;
          opacity: 0.1;
          transition: all 0.5s ease;
          pointer-events: none;
          z-index: 0;
        }

        &.model-total { background: linear-gradient(135deg, #6366f1 0%, #4338ca 100%); }
        &.user-total { background: linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%); }
        &.used { background: linear-gradient(135deg, #10b981 0%, #047857 100%); }
        &.model-available { background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%); }
        &.user-available { background: linear-gradient(135deg, #f59e0b 0%, #b45309 100%); }
        &.rate { background: linear-gradient(135deg, #f43f5e 0%, #be123c 100%); }

        .card-content {
          display: flex;
          align-items: center;
          gap: 20px;
          position: relative;
          z-index: 1;

          .card-icon-wrapper {
            width: 56px;
            height: 56px;
            background: rgba(255, 255, 255, 0.25);
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 28px;
            color: #fff;
            flex-shrink: 0;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }

          .card-info {
            flex: 1;
            .card-title {
              font-size: 14px;
              color: rgba(255, 255, 255, 0.9);
              margin-bottom: 8px;
              font-weight: 500;
              white-space: nowrap;
              text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            }

            .card-value-wrapper {
              display: flex;
              align-items: baseline;
              gap: 6px;

              .card-value {
                font-size: 32px;
                font-weight: 800;
                color: #fff;
                line-height: 1;
                letter-spacing: -0.5px;
                text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              }

              .card-unit {
                font-size: 14px;
                color: rgba(255, 255, 255, 0.7);
                font-weight: 500;
              }
            }

            .card-change {
              margin-top: 10px;
              display: inline-flex;
              align-items: center;
              font-size: 12px;
              background: rgba(255, 255, 255, 0.2);
              padding: 2px 8px;
              border-radius: 12px;
              color: #fff;
              backdrop-filter: blur(4px);

              .icon-arrow { margin-right: 2px; }
            }
          }
        }
      }
    }

    .main-content-grid {
      display: flex;
      flex-direction: column;
      gap: 12px;
      flex: 1;

      .top-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 12px;
        min-height: 400px;
        
        .trend-chart-card {
          grid-column: span 2;
        }

        .trend-chart-card, .analysis-card {
          border-radius: 12px;
          border: 1px solid #f1f5f9;
          display: flex;
          flex-direction: column;
          box-shadow: none !important;
          background: #fff;
          transition: all 0.2s;
          
          &:hover {
            border-color: #e2e8f0;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
          }
          
          :deep(.el-card__header) {
            padding: 0;
            border-bottom: 1px solid #f1f5f9;
          }

          .card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px 20px;

            .header-title-wrapper {
              display: flex;
              align-items: center;
              gap: 10px;
              color: #1e293b;
              font-weight: 600;
              font-size: 15px;

              i {
                color: #409eff;
                font-size: 18px;
              }
            }

            .card-header-actions {
              display: flex;
              align-items: center;
              gap: 8px;

              .mini-select {
                width: 100px;
                :deep(.el-input__inner) {
                  background-color: #f8fafc;
                  border: 1px solid #f1f5f9;
                  border-radius: 6px;
                  height: 32px;
                  line-height: 32px;
                  font-size: 13px;
                }
                &.granularity-select {
                  width: 70px;
                }
              }

              .el-checkbox {
                margin-right: 0;
                font-weight: 500;
                :deep(.el-checkbox__label) {
                  font-size: 13px;
                  color: #64748b;
                  padding-left: 4px;
                }
              }
            }
          }

          :deep(.el-card__body) {
            flex: 1;
            padding: 15px;
            display: flex;
            flex-direction: column;
          }
        }

        .header-title-wrapper {
            display: flex;
            align-items: center;
            gap: 12px;

            .title-icon-box {
              width: 32px;
              height: 32px;
              background: #f0f7ff;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #409eff;
              font-size: 18px;
            }

            .title-text-box {
              display: flex;
              align-items: center;
              gap: 8px;

              .header-main-title {
                font-size: 16px;
                font-weight: 600;
                color: #303133;
              }

              .filter-tag {
                border-radius: 4px;
                font-weight: 500;
              }
            }
          }

          .chart-controls, .card-header-actions {
            display: flex;
            align-items: center;
            gap: 12px;

            .control-item {
              display: flex;
              align-items: center;
            }

            .custom-select {
              :deep(.el-input__inner) {
                background-color: #f5f7fa;
                border: 1px solid transparent;
                border-radius: 6px;
                transition: all 0.2s;
                height: 28px;
                line-height: 28px;
                font-size: 12px;
                
                &:hover {
                  background-color: #ecf5ff;
                }
                
                &:focus {
                  background-color: #fff;
                  border-color: #409eff;
                }
              }

              &.model-select { width: 160px; }
              &.time-select { width: 90px; }
              &.metric-select { width: 110px; }
            }

            .date-picker-wrapper {
              .custom-date-picker {
                width: 220px;
                background-color: #f5f7fa;
                border: 1px solid transparent;
                border-radius: 6px;
                
                :deep(.el-range-input) {
                  background-color: transparent;
                }
                
                &.is-active {
                  background-color: #fff;
                  border-color: #409eff;
                }
              }
            }

            .custom-radio-group {
              :deep(.el-radio-button__inner) {
                background-color: #f5f7fa;
                border: none;
                margin: 0 2px;
                border-radius: 6px !important;
                padding: 7px 10px;
                color: #909399;
                transition: all 0.2s;
                display: flex;
                align-items: center;
                justify-content: center;

                &:hover {
                  color: #409eff;
                  background-color: #ecf5ff;
                }

                i {
                  font-size: 14px;
                }
              }

              :deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
                background-color: #409eff;
                color: #fff;
                box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
              }
            }

            .action-icon-btn {
              background-color: #f5f7fa;
              border: none;
              border-radius: 6px;
              padding: 7px 10px;
              color: #909399;
              transition: all 0.2s;

              &:hover {
                color: #409eff;
                background-color: #ecf5ff;
              }

              &.el-button--primary {
                background-color: #409eff;
                color: #fff;
                box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
              }

              i {
                font-size: 14px;
              }
            }
          }
        }

        .chart-container, .chart-container-mini {
          flex: 1;
          width: 100%;
          position: relative;
          display: flex;
          flex-direction: column;
        }

        .mini-loading {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.7);
          z-index: 10;
          font-size: 24px;
          color: #409eff;
        }
      }

      .middle-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;

        .usage-ranking-card {
          border-radius: 12px;
          border: none;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;

          :deep(.el-card__header) {
            padding: 12px 16px;
            border-bottom: 1px solid #f0f2f5;
            font-weight: 600;
          }

          :deep(.el-card__body) {
            padding: 0;
          }

          .ranking-container {
            padding: 8px;
            height: 300px;
            overflow: hidden;
            
            :deep(.el-table) {
              &::before { display: none; }
              tr { background-color: transparent; }
              th { background-color: #fafafa; font-weight: 600; font-size: 13px; }
              
              .clickable-row {
                cursor: pointer;
                transition: background-color 0.2s;
                &:hover > td {
                  background-color: #f5f7fa !important;
                }
              }
            }

            .rank-badge {
              width: 24px;
              height: 24px;
              border-radius: 6px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              font-weight: bold;
              background: #f0f2f5;
              color: #909399;

              &.rank-1 { background: #fff7e6; color: #ffa940; border: 1px solid #ffd591; }
              &.rank-2 { background: #f0f5ff; color: #2f54eb; border: 1px solid #adc6ff; }
              &.rank-3 { background: #f6ffed; color: #52c41a; border: 1px solid #b7eb8f; }
            }

            .tokens-value {
              font-family: 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
              font-weight: 600;
              color: #409eff;
            }
          }
        }
      }
    }

    .alerts-card-full {
      flex: 1;
      display: flex;
      flex-direction: column;
      border-radius: 12px;
      border: 1px solid #f1f5f9;
      box-shadow: none !important;
      background: #fff;
      transition: all 0.2s;
      
      &:hover {
        border-color: #e2e8f0;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
      }
      
      :deep(.el-card__header) {
        padding: 0;
        border-bottom: 1px solid #f1f5f9;
        
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          color: #1e293b;
          font-weight: 600;
          font-size: 15px;

          .header-title-wrapper {
            display: flex;
            align-items: center;
            gap: 10px;

            i {
              color: #409eff;
              font-size: 18px;
            }
          }
        }
      }

      :deep(.el-card__body) {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 15px;
      }

      .alert-table-container {
        flex: 1;
        display: flex;
        flex-direction: column;

        :deep(.el-table) {
          flex: 1;
          
          th.el-table__cell {
            background-color: #f8fafc;
            color: #475569;
            font-weight: 600;
            height: 50px;
          }

          .el-table__row {
            td.el-table__cell {
              padding: 12px 0;
            }
            
            &.el-table__row--striped td.el-table__cell {
              background-color: #f8fafc;
            }
          }
        }

        .pagination-container {
          margin-top: 15px;
          display: flex;
          justify-content: flex-end;
          flex-shrink: 0;

          :deep(.el-pagination) {
            padding: 0;
            font-weight: 500;
            
            .btn-prev, .btn-next, .el-pager li {
              background-color: #f8fafc !important;
              border-radius: 6px !important;
              border: 1px solid #f1f5f9 !important;
              margin: 0 3px;
              
              &:hover {
                color: #409eff !important;
                border-color: #409eff !important;
              }
              
              &.active {
                background-color: #409eff !important;
                color: #fff !important;
                border-color: #409eff !important;
              }
            }
          }
        }
      }
    }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
}

.clickable-row {
  cursor: pointer;
}

@media screen and (max-width: 1400px) {
  .resource-dashboard-container {
    .dashboard-content {
      .overview-cards {
        gap: 12px;
        :deep(.el-card__body) {
          padding: 16px;
        }
        .card-content {
          .card-icon-wrapper {
            width: 40px;
            height: 40px;
            border-radius: 12px;
            margin-right: 12px;
            font-size: 20px;
          }
          .card-info {
            .card-title {
              font-size: 12px;
              margin-bottom: 4px;
            }
            .card-value {
              font-size: 20px;
              margin-bottom: 4px;
            }
          }
        }
      }
    }
  }
}
.alert-detail-dialog {
  .el-dialog__body {
    padding: 20px 25px;
  }
}

.alert-header-info {
  .alert-title-row {
    margin-bottom: 12px;
    display: flex;
    align-items: flex-start;
    
    .detail-label {
      font-weight: 600;
      color: #606266;
      margin-right: 10px;
      white-space: nowrap;
    }
    
    .title-text {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      line-height: 1.4;
    }
  }
}

.alert-body-info {
  .info-item {
    margin-bottom: 18px;
    display: flex;
    flex-direction: column;
    
    .item-label {
      font-size: 13px;
      color: #909399;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      
      i {
        margin-right: 6px;
      }
    }
    
    .item-value {
      font-size: 14px;
      color: #303133;
      line-height: 1.6;
    }
    
    .content-box {
      background: #f8f9fa;
      padding: 12px 15px;
      border-radius: 6px;
      border-left: 4px solid #409eff;
    }
    
    &.highlight-item {
      background: #f0f7ff;
      padding: 12px 15px;
      border-radius: 8px;
      
      .link-value {
        display: flex;
        align-items: center;
        justify-content: space-between;
        
        .name-text {
          font-weight: 600;
          color: #409eff;
        }
        
        .jump-btn {
          padding: 4px 10px;
        }
      }
    }
  }
}

</style>
